import { ChevronLeftIcon } from '@heroicons/react/16/solid'
import { useEffect, useState } from 'react'
import { SavedEntryNotice } from '../farmlogs/entry-check'
import { Button } from '../catalyst/button'
import { Heading } from '../catalyst/heading'
import { Link } from '../catalyst/link'
import { Select } from '../catalyst/select'
import { Text } from '../catalyst/text'
import { Shell } from '../pages/shell'
import { cadenceLines, exportFarmForm, farmBadgeProps } from './canadagap'
import { ComplianceBadge } from './compliance-badge'
import { useFarms } from './farms'
import type { CanadaGapFarmFormKey, CanadaGapFarmStatus } from './types'

export const formatDate = (iso: string) =>
  new Date(`${iso}T00:00:00`).toLocaleDateString(undefined, { year: 'numeric', month: 'short', day: 'numeric' })

/** One timeline item: a dated entry (or a group, like a Form M round) with its own content. */
export type TimelineEntry = { key: string; date: string; onClick?: () => void; content: React.ReactNode }

/**
 * The page shell for a farm-level log (CanadaGAP Forms G, I, J, K, L, M), laid out like the per-field
 * log pages: back link to the farm, heading with the live CanadaGAP badge, year select, an optional
 * name filter (building / facility / equipment), export, add, the cadence gaps, and the timeline.
 * Each page owns its data, dialog and status, and passes them in.
 */
export function FarmLogLayout({
  orgId,
  farmId,
  form,
  title,
  formName,
  addLabel,
  addHref,
  onAdd,
  year,
  onYearChange,
  nameFilter,
  status,
  loading,
  error,
  entries,
  emptyText,
  saved,
  children,
}: {
  orgId: string | undefined
  farmId: string | undefined
  form: CanadaGapFarmFormKey
  title: string
  formName: string
  addLabel: string
  /** Where adding goes: the logging wizard (preferred), or a handler. */
  addHref?: string
  onAdd?: () => void
  year: number
  onYearChange: (year: number) => void
  nameFilter?: { label: string; options: string[]; value: string; onChange: (value: string) => void }
  status: CanadaGapFarmStatus | null
  loading: boolean
  error: string | null
  entries: TimelineEntry[]
  emptyText: string
  /** Entries just saved, for the after-save notice (see useSavedEntries). */
  saved?: { ids: string[]; onDismiss: () => void }
  /** Dialogs. */
  children?: React.ReactNode
}) {
  const { getFarm } = useFarms()
  const [exporting, setExporting] = useState(false)
  const [exportError, setExportError] = useState<string | null>(null)
  const farm = farmId ? getFarm(farmId) : undefined
  const currentYear = new Date().getFullYear()
  const years = [currentYear - 2, currentYear - 1, currentYear]
  const filterName = nameFilter?.value || undefined
  const gaps = cadenceLines(status, form).filter((l) => !filterName || l.startsWith(`${filterName} — `))

  async function handleExport() {
    if (!orgId || !farmId) return
    setExporting(true)
    setExportError(null)
    try {
      await exportFarmForm(orgId, farmId, form, year, filterName)
    } catch (err) {
      setExportError(err instanceof Error ? err.message : 'Export failed.')
    } finally {
      setExporting(false)
    }
  }

  return (
    <Shell>
      <Link href={`/farms/${farmId}`} className="inline-flex items-center gap-1 text-sm/6 font-medium text-zinc-500 dark:text-zinc-400">
        <ChevronLeftIcon className="size-4" />
        {farm?.name ?? 'Farm'}
      </Link>

      <div className="mt-4 flex flex-wrap items-start justify-between gap-4">
        <div>
          <Heading>{title}</Heading>
          <div className="flex flex-wrap gap-1.5">
            <ComplianceBadge framework="CanadaGAP" formCode={form.toUpperCase()} formName={formName} {...farmBadgeProps(status, form)} />
          </div>
        </div>
        <div className="flex flex-wrap items-center gap-3">
          <Select value={year} onChange={(e) => onYearChange(Number(e.target.value))} className="w-28" aria-label="Year">
            {years.map((y) => (
              <option key={y} value={y}>
                {y}
              </option>
            ))}
          </Select>
          {nameFilter && (
            <Select value={nameFilter.value} onChange={(e) => nameFilter.onChange(e.target.value)} className="w-48" aria-label={nameFilter.label}>
              <option value="">All {nameFilter.label.toLowerCase()}s</option>
              {nameFilter.options.map((n) => (
                <option key={n} value={n}>
                  {n}
                </option>
              ))}
            </Select>
          )}
          <Button outline onClick={handleExport} disabled={exporting || entries.length === 0}>
            {exporting ? 'Exporting…' : `Export ${form.toUpperCase()}`}
          </Button>
          <Button {...(addHref ? { href: addHref } : { onClick: onAdd })}>{addLabel}</Button>
        </div>
      </div>

      {exportError && <Text className="mt-3 text-red-600 dark:text-red-400">{exportError}</Text>}

      {saved && saved.ids.length > 0 && status && (
        <SavedEntryNotice
          formCode={form.toUpperCase()}
          issues={status.forms[form].issues.filter((i) => saved.ids.includes(i.entryId))}
          savedCount={saved.ids.length}
          onDismiss={saved.onDismiss}
        />
      )}

      {gaps.length > 0 && (
        <div className="mt-6 rounded-lg bg-amber-50 p-3 ring-1 ring-amber-600/20 dark:bg-amber-900/20 dark:ring-amber-500/20">
          <p className="text-sm font-medium text-amber-800 dark:text-amber-300">Missed checks in {year}</p>
          <ul className="mt-1 space-y-0.5">
            {gaps.map((g) => (
              <li key={g} className="text-sm text-amber-700 dark:text-amber-400">
                {g}
              </li>
            ))}
          </ul>
        </div>
      )}

      {error ? (
        <div className="mt-6 rounded-lg border border-dashed border-red-300 p-10 text-center dark:border-red-900">
          <Text>{error}</Text>
        </div>
      ) : loading ? (
        <div className="mt-6 rounded-lg border border-dashed border-zinc-950/15 p-10 text-center dark:border-white/15">
          <Text>Loading…</Text>
        </div>
      ) : entries.length === 0 ? (
        <div className="mt-8 rounded-lg border border-dashed border-zinc-950/15 p-10 text-center dark:border-white/15">
          <Text>{emptyText}</Text>
        </div>
      ) : (
        <ul className="mt-8 -mb-8">
          {entries.map((entry, index) => {
            const body = (
              <>
                <p className="text-sm font-semibold text-zinc-950 dark:text-white">{formatDate(entry.date)}</p>
                {entry.content}
              </>
            )
            return (
              <li key={entry.key}>
                <div className="relative pb-8">
                  {index < entries.length - 1 && (
                    <span className="absolute left-4 top-4 -ml-px h-full w-0.5 bg-zinc-200 dark:bg-zinc-700" aria-hidden="true" />
                  )}
                  <div className="relative flex gap-4">
                    <div className="flex size-8 shrink-0 items-center justify-center rounded-full bg-zinc-100 ring-1 ring-zinc-950/10 dark:bg-zinc-800 dark:ring-white/10">
                      <span className="size-2 rounded-full bg-zinc-400 dark:bg-zinc-500" />
                    </div>
                    {entry.onClick ? (
                      <button
                        type="button"
                        onClick={entry.onClick}
                        className="-m-2 min-w-0 flex-1 rounded-lg p-2 pt-2.5 text-left hover:bg-zinc-950/[2.5%] dark:hover:bg-white/[2.5%]"
                      >
                        {body}
                      </button>
                    ) : (
                      <div className="min-w-0 flex-1 pt-0.5">{body}</div>
                    )}
                  </div>
                </div>
              </li>
            )
          })}
        </ul>
      )}

      {children}
    </Shell>
  )
}

/**
 * The entries just saved from a dialog, for FarmLogLayout's after-save notice. Cleared when another
 * dialog opens or the year changes, since the notice is about those entries only.
 */
export function useSavedEntries(dialogOpen: boolean, year: number) {
  const [ids, setIds] = useState<string[]>([])
  useEffect(() => {
    if (dialogOpen) setIds([])
  }, [dialogOpen])
  useEffect(() => setIds([]), [year])
  return { ids, setIds, onDismiss: () => setIds([]) }
}

/** A dialog's open state: closed, adding, or editing one row. */
export type DialogState<T> = { log?: T } | null

/** Newest first, by entry date then creation. */
export function byDateDesc<T extends { createdAt: string }>(dateOf: (row: T) => string) {
  return (a: T, b: T) => dateOf(b).localeCompare(dateOf(a)) || b.createdAt.localeCompare(a.createdAt)
}

/** A text field's value for an input: '' for null. */
export const text = (v: string | null | undefined) => v ?? ''
/** An input's value for the API: null when blank (clears it on edit; dropped on create). */
export const orNull = (v: string) => v.trim() || null
